/**
 * Configuration Manager for Phantasy Live2D SDK
 * Handles merging defaults, validation, and updates
 */

import type {
  PhantasyLive2DConfig,
  SDKDefaults,
  Live2DModelConfig,
  TTSConfig,
  LipSyncConfig,
  FeatureConfig,
} from '../types/config';
import { createLogger } from '../utils/logger';

export class ConfigManager {
  private logger: ReturnType<typeof createLogger>;
  private currentConfig: Required<PhantasyLive2DConfig> = {} as Required<PhantasyLive2DConfig>;

  constructor(private defaults: SDKDefaults) {
    this.logger = createLogger('ConfigManager');
  }

  /**
   * Merge user config with defaults to create complete configuration
   */
  mergeConfig(userConfig: PhantasyLive2DConfig): Required<PhantasyLive2DConfig> {
    const merged: Required<PhantasyLive2DConfig> = {
      container: userConfig.container,
      model: this.normalizeModelConfig(userConfig.model),
      tts: this.mergeTTSConfig(userConfig.tts),
      lipSync: this.mergeLipSyncConfig(userConfig.lipSync),
      features: this.mergeFeatureConfig(userConfig.features),
      debug: userConfig.debug ?? false,
      onReady: userConfig.onReady ?? (() => {}),
      onError: userConfig.onError ?? (() => {}),
    };

    this.validateConfig(merged);
    this.currentConfig = merged;

    this.logger.debug('Configuration merged:', merged);
    return merged;
  }

  /**
   * Update existing configuration
   */
  updateConfig(updates: Partial<PhantasyLive2DConfig>): Partial<Required<PhantasyLive2DConfig>> {
    const updatedFields: Partial<Required<PhantasyLive2DConfig>> = {};

    if (updates.tts) {
      updatedFields.tts = this.mergeTTSConfig(updates.tts);
      this.currentConfig.tts = updatedFields.tts;
    }

    if (updates.lipSync) {
      updatedFields.lipSync = this.mergeLipSyncConfig(updates.lipSync);
      this.currentConfig.lipSync = updatedFields.lipSync;
    }

    if (updates.features) {
      updatedFields.features = this.mergeFeatureConfig(updates.features);
      this.currentConfig.features = updatedFields.features;
    }

    if (updates.model) {
      updatedFields.model = this.normalizeModelConfig(updates.model);
      this.currentConfig.model = updatedFields.model;
    }

    if (updates.debug !== undefined) {
      updatedFields.debug = updates.debug;
      this.currentConfig.debug = updates.debug;
    }

    this.logger.debug('Configuration updated:', updatedFields);
    return updatedFields;
  }

  /**
   * Get current complete configuration
   */
  getConfig(): Required<PhantasyLive2DConfig> {
    return { ...this.currentConfig };
  }

  private normalizeModelConfig(model: string | Live2DModelConfig): Live2DModelConfig {
    if (typeof model === 'string') {
      return {
        path: model,
        scale: 0.3,
        position: { x: 0.5, y: 0.5 },
        expressions: {},
      };
    }

    return {
      path: model.path,
      scale: model.scale ?? 0.3,
      position: model.position ?? { x: 0.5, y: 0.5 },
      expressions: model.expressions ?? {},
      responsive: model.responsive ?? undefined,
    };
  }

  private mergeTTSConfig(userTTS?: Partial<TTSConfig>): TTSConfig {
    const defaultTTS: TTSConfig = {
      primary: {
        provider: 'alkahest',
        apiUrl: 'https://api.alkahest.ai',
      },
      fallback: {
        provider: 'gptsovits',
        apiUrl: 'http://localhost:9880',
      },
      timeout: 30000,
      ...this.defaults.tts,
    };

    if (!userTTS) return defaultTTS;

    return {
      primary: { ...defaultTTS.primary, ...userTTS.primary },
      fallback: userTTS.fallback
        ? { ...defaultTTS.fallback!, ...userTTS.fallback }
        : defaultTTS.fallback,
      timeout: userTTS.timeout ?? defaultTTS.timeout,
    };
  }

  private mergeLipSyncConfig(userLipSync?: Partial<LipSyncConfig>): LipSyncConfig {
    const defaultLipSync: LipSyncConfig = {
      enabled: true,
      sensitivity: 0.8,
      smoothing: 0.3,
      amplification: 1.3,
      parameterNames: [
        'ParamMouthOpenY',
        'PARAM_MOUTH_OPEN_Y',
        'Param_Mouth_Open_Y',
        'ParamMouthForm',
        'PARAM_MOUTH_FORM',
        'Param_Mouth_Form',
        'ParamMouthOpen',
        'PARAM_MOUTH_OPEN',
        'Param_Mouth_Open',
      ],
      ...this.defaults.lipSync,
    };

    return { ...defaultLipSync, ...userLipSync };
  }

  private mergeFeatureConfig(userFeatures?: Partial<FeatureConfig>): FeatureConfig {
    const defaultFeatures: FeatureConfig = {
      mouseTracking: false,
      breathing: true,
      autoExpressions: false,
      responsivePositioning: true,
      orbitControls: {
        enabled: true,
        enableZoom: true,
        enablePan: true,
        zoomSpeed: 1.0,
        panSpeed: 1.0,
        minZoom: 0.1,
        maxZoom: 3.0,
        zoomSensitivity: 0.01, // Increased for more responsive zooming
        mouseButtons: {
          pan: 0, // Left mouse button
          zoom: true, // Mouse wheel
        },
        touchGestures: {
          pinchZoom: true,
          panTouch: true,
        },
      },
      ...this.defaults.features,
    };

    return { ...defaultFeatures, ...userFeatures };
  }

  private validateConfig(config: Required<PhantasyLive2DConfig>): void {
    // Validate container
    if (!config.container) {
      throw new Error('Container is required');
    }

    // Validate model path
    const modelConfig = config.model as Live2DModelConfig;
    if (!modelConfig.path) {
      throw new Error('Model path is required');
    }

    // Validate TTS configuration
    if (!config.tts.primary.provider) {
      throw new Error('Primary TTS provider is required');
    }

    // Validate position values
    const pos = modelConfig.position;
    if (pos && (pos.x < 0 || pos.x > 1 || pos.y < 0 || pos.y > 2)) {
      this.logger.warn('Model position values should typically be between 0-1 for x and 0-2 for y');
    }

    // Validate scale
    if (modelConfig.scale !== undefined && (modelConfig.scale <= 0 || modelConfig.scale > 2)) {
      this.logger.warn('Model scale should typically be between 0.1 and 1.0');
    }

    // Validate lip sync values
    const lipSync = config.lipSync;
    if (lipSync.sensitivity < 0 || lipSync.sensitivity > 2) {
      this.logger.warn('Lip sync sensitivity should typically be between 0 and 2');
    }

    if (lipSync.amplification < 0.5 || lipSync.amplification > 3) {
      this.logger.warn('Lip sync amplification should typically be between 0.5 and 3');
    }
  }
}
